<!-- eslint-disable vue/no-mutating-props -- shared editForm object from parent (same as prior inline modal) -->
<template>
  <div v-if="show" class="manager-company-records-modal-backdrop">
    <div
      ref="modalRoot"
      class="manager-company-records-modal"
      :class="{
        'manager-company-records-modal--timesheet': isTimesheetsView,
        'manager-company-records-modal--invoice': isInvoicesView
      }"
      :style="modalInlineStyle"
      role="dialog"
      aria-modal="true"
    >
      <div class="manager-company-records-modal-header">
        <h2>{{ title }}</h2>
        <button type="button" class="manager-company-records-modal-close" aria-label="Close" @click="onClose">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="manager-company-records-modal-body">
        <div v-if="isInvoicesView && editRow" class="manager-invoice-edit-details">
          <div class="manager-invoice-edit-details-head">
            <h3 class="manager-invoice-edit-details-title">Invoice details</h3>
            <p class="manager-invoice-edit-details-meta">
              <span class="manager-invoice-edit-details-meta-label">Invoice #</span>
              <span class="manager-invoice-edit-details-meta-value">{{ editRow.invoiceNo || '—' }}</span>
            </p>
          </div>
          <div class="manager-invoice-edit-details-split">
            <dl class="manager-invoice-edit-dl">
              <dt>Customer</dt>
              <dd>{{ invoiceCustomerDisplay }}</dd>
              <dt>Subtotal</dt>
              <dd>{{ formatMoney(editRow.subtotal) }}</dd>
              <dt>Tax</dt>
              <dd>{{ formatMoney(editRow.taxAmount) }}</dd>
              <dt>Bonus</dt>
              <dd>{{ formatMoney(editRow.bonusAmount) }}</dd>
              <dt>Discount</dt>
              <dd>{{ formatMoney(editRow.discountAmount) }}</dd>
              <dt>Total</dt>
              <dd>{{ formatMoney(editRow.totalAmount) }}</dd>
              <dt>Paid</dt>
              <dd>{{ formatMoney(editRow.paidAmount) }}</dd>
              <dt>Balance</dt>
              <dd>{{ formatMoney(editRow.balanceAmount) }}</dd>
            </dl>
            <dl class="manager-invoice-edit-dl">
              <dt>Payment terms</dt>
              <dd>{{ invoicePaymentTermDisplay }}</dd>
              <dt>Recurring</dt>
              <dd>{{ editRow.isRecurring ? 'Yes' : 'No' }}</dd>
              <template v-if="editRow.isRecurring">
                <dt>Frequency</dt>
                <dd>{{ editRow.recurringFrequency || '—' }}</dd>
                <dt>Next recurring</dt>
                <dd>{{ formatUsDate(editRow.nextRecurringDate) }}</dd>
              </template>
              <dt>Created</dt>
              <dd>{{ formatUsDateTime(editRow.createdAt) }}</dd>
              <dt>Updated</dt>
              <dd>{{ formatUsDateTime(editRow.updatedAt) }}</dd>
            </dl>
          </div>
          <div class="manager-invoice-edit-lines">
            <h4 class="manager-invoice-edit-lines-title">Line items</h4>
            <div class="manager-invoice-edit-lines-table-wrap">
              <table v-if="invoiceLineItems.length" class="manager-invoice-edit-lines-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Description</th>
                    <th class="manager-invoice-edit-num">Hours / Qty</th>
                    <th class="manager-invoice-edit-num">Rate / Unit price</th>
                    <th class="manager-invoice-edit-num">Amount</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in invoiceLineItems" :key="line.id || line.name + '-' + line.totalAmount">
                    <td>{{ line.name || '—' }}</td>
                    <td class="manager-invoice-edit-desc">{{ line.description || '—' }}</td>
                    <td class="manager-invoice-edit-num">{{ invoiceLineQtyOrHours(line) }}</td>
                    <td class="manager-invoice-edit-num">{{ invoiceLineUnitOrHourlyRate(line) }}</td>
                    <td class="manager-invoice-edit-num">{{ formatMoney(line.totalAmount) }}</td>
                    <td>{{ line.itemType || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="manager-invoice-edit-lines-empty">No line items</p>
            </div>
          </div>
        </div>
        <div v-if="isTimesheetsView && editRow" class="manager-ts-edit-details">
          <div class="manager-ts-edit-details-head">
            <h3 class="manager-ts-edit-details-title">Timesheet details</h3>
            <p class="manager-ts-edit-details-created">
              <span class="manager-ts-edit-details-created-label">Created</span>
              <span class="manager-ts-edit-details-created-value">{{ formatUsDateTime(editRow.createdAt) }}</span>
            </p>
          </div>
          <div class="manager-ts-edit-details-split">
            <dl
              class="manager-ts-edit-details-grid manager-ts-edit-details-grid--compact manager-ts-edit-details-grid--stack-column"
            >
              <dt>Consultant ID</dt>
              <dd>{{ editRow.consultantId || '—' }}</dd>
              <dt>Consultant name</dt>
              <dd>{{ editRow.consultantName || '—' }}</dd>
              <dt>Consultant type</dt>
              <dd>{{ editRow.consultantType || '—' }}</dd>
            </dl>
            <dl
              class="manager-ts-edit-details-grid manager-ts-edit-details-grid--compact manager-ts-edit-details-grid--stack-column"
            >
              <dt>Timesheet ID</dt>
              <dd>{{ editRow.id }}</dd>
              <dt>Customer</dt>
              <dd>{{ partyNameOnly(editRow.customerName) }}</dd>
              <dt>Vendor</dt>
              <dd>{{ partyNameOnly(editRow.vendorName) }}</dd>
              <template v-if="editTimesheetListSource === 'hr' && editRow.timesheetFrom">
                <dt>Source</dt>
                <dd>{{ editRow.timesheetFrom }}</dd>
              </template>
              <dt>HR timesheet ID</dt>
              <dd>{{ editRow.hrTimesheetId || '—' }}</dd>
            </dl>
          </div>
          <p v-if="!canEditTimesheetPeriod" class="manager-ts-edit-period-lock-hint">
            {{ timesheetPeriodLockHint }}
          </p>
          <div class="manager-ts-edit-period-row">
            <label class="manager-company-records-field manager-ts-edit-field--grow">
              <span>From</span>
              <div class="manager-ts-edit-modal-field-control">
                <template v-if="canEditTimesheetPeriod">
                  <div class="manager-ts-datepicker-wrap manager-ts-datepicker-wrap--modal-field">
                    <DatePicker v-model="editFormFromDateUI" placeholder="MM/DD/YYYY" />
                  </div>
                </template>
                <div v-else class="manager-ts-edit-period-readonly manager-ts-edit-period-readonly--full">
                  {{ formatUsDate(editForm.fromDate) }}
                </div>
              </div>
            </label>
            <label class="manager-company-records-field manager-ts-edit-field--grow">
              <span>To</span>
              <div class="manager-ts-edit-modal-field-control">
                <template v-if="canEditTimesheetPeriod">
                  <div class="manager-ts-datepicker-wrap manager-ts-datepicker-wrap--modal-field">
                    <DatePicker v-model="editFormToDateUI" placeholder="MM/DD/YYYY" />
                  </div>
                </template>
                <div v-else class="manager-ts-edit-period-readonly manager-ts-edit-period-readonly--full">
                  {{ formatUsDate(editForm.toDate) }}
                </div>
              </div>
            </label>
            <label class="manager-company-records-field manager-ts-edit-field--hours">
              <span>Hours</span>
              <div class="manager-ts-edit-modal-field-control">
                <input
                  v-if="canEditTimesheetPeriod"
                  v-model.number="editForm.hours"
                  class="manager-ts-edit-hours-input manager-ts-edit-hours-input--modal-field"
                  type="number"
                  min="0"
                  step="0.01"
                />
                <div v-else class="manager-ts-edit-period-readonly manager-ts-edit-period-readonly--full">
                  {{ formatTimesheetHoursReadonly(editForm.hours) }}
                </div>
              </div>
            </label>
          </div>
          <div class="manager-ts-edit-status-row manager-ts-edit-status-row--after-period">
            <template v-for="field in timesheetEditFields" :key="field.key">
              <label
                v-if="field.type === 'checkbox'"
                class="manager-company-records-field manager-company-records-field--check manager-ts-edit-status-row__full"
              >
                <input v-model="editForm[field.key]" type="checkbox" />
                <span>{{ field.label }}</span>
              </label>
              <label v-else class="manager-company-records-field manager-ts-edit-field--grow">
                <span>{{ field.label }}</span>
                <textarea
                  v-if="field.type === 'textarea'"
                  v-model="editForm[field.key]"
                  rows="3"
                />
                <select v-else-if="field.type === 'select'" v-model="editForm[field.key]">
                  <option v-for="opt in field.options" :key="String(opt.value)" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <input v-else v-model="editForm[field.key]" :type="field.type || 'text'" />
              </label>
            </template>
          </div>
          <div class="manager-ts-edit-attachments">
            <h4 class="manager-ts-edit-attachments-title">Attachments</h4>
            <p v-if="timesheetAttachmentError" class="manager-company-records-error manager-ts-edit-attachment-error">
              {{ timesheetAttachmentError }}
            </p>
            <p
              v-if="timesheetAttachmentSuccess"
              class="manager-ts-edit-attachment-success"
              role="status"
            >
              {{ timesheetAttachmentSuccess }}
            </p>
            <div class="manager-ts-edit-attachments-toolbar">
              <input
                ref="tsAttachmentFileInput"
                type="file"
                class="manager-ts-edit-attachment-file-input"
                tabindex="-1"
                aria-hidden="true"
                @change="onTsAttachmentFileChange"
              />
              <button
                type="button"
                class="manager-ts-edit-attachment-btn"
                :disabled="timesheetAttachmentsBusy"
                @click="pickTimesheetAttachmentFile"
              >
                <i class="bi bi-paperclip"></i> Add attachment
              </button>
            </div>
            <p v-if="!timesheetAttachments.length" class="manager-ts-edit-attachments-empty">No attachments yet</p>
            <ul v-else class="manager-ts-edit-attachments-list">
              <li v-for="att in timesheetAttachments" :key="att.id" class="manager-ts-edit-attachment-row">
                <div v-if="tsAttRenameId === att.id" class="manager-ts-edit-attachment-rename-row">
                  <input v-model="tsAttRenameValue" type="text" maxlength="500" :disabled="timesheetAttachmentsBusy" />
                  <button
                    type="button"
                    class="manager-ts-edit-attachment-btn"
                    :disabled="timesheetAttachmentsBusy || !String(tsAttRenameValue || '').trim()"
                    @click="submitTimesheetAttachmentRename(att)"
                  >
                    Save name
                  </button>
                  <button
                    type="button"
                    class="manager-ts-edit-attachment-btn"
                    :disabled="timesheetAttachmentsBusy"
                    @click="cancelTimesheetAttachmentRename"
                  >
                    Cancel
                  </button>
                </div>
                <template v-else>
                  <HRTimesheetAttachmentRow
                    :attachment="att"
                    :clickable-stored-name="true"
                    @open-stored="$emit('view-attachment', $event, editTimesheetListSource)"
                  >
                    <template #actions>
                      <button
                        type="button"
                        class="manager-ts-edit-attachment-btn"
                        @click.prevent="$emit('view-attachment', att, editTimesheetListSource)"
                      >
                        <i class="bi bi-eye"></i> View
                      </button>
                      <button
                        type="button"
                        class="manager-ts-edit-attachment-btn"
                        @click.prevent="$emit('download-attachment', att, editTimesheetListSource)"
                      >
                        <i class="bi bi-download"></i> Download
                      </button>
                      <button
                        type="button"
                        class="manager-ts-edit-attachment-btn"
                        :disabled="timesheetAttachmentsBusy"
                        @click.prevent="startTimesheetAttachmentRename(att)"
                      >
                        <i class="bi bi-pencil"></i> Edit
                      </button>
                      <button
                        type="button"
                        class="manager-ts-edit-attachment-btn manager-ts-edit-attachment-btn--danger"
                        :disabled="timesheetAttachmentsBusy"
                        @click.prevent="$emit('delete-timesheet-attachment', { attachment: att })"
                      >
                        <i class="bi bi-trash"></i> Delete
                      </button>
                    </template>
                  </HRTimesheetAttachmentRow>
                </template>
              </li>
            </ul>
          </div>
        </div>
        <template v-if="!isTimesheetsView">
          <p v-if="isInvoicesView" class="manager-invoice-due-auto-hint">
            Changing the invoice date updates the due date using this invoice’s payment term net days (same as new invoice).
          </p>
          <h3 v-if="isInvoicesView" class="manager-invoice-edit-fields-heading">Editable fields</h3>
          <template v-for="field in recordEditFields" :key="field.key">
            <label
              v-if="field.type === 'checkbox'"
              class="manager-company-records-field manager-company-records-field--check"
            >
              <input v-model="editForm[field.key]" type="checkbox" />
              <span>{{ field.label }}</span>
            </label>
            <label v-else class="manager-company-records-field">
              <span>{{ field.label }}</span>
              <textarea
                v-if="field.type === 'textarea'"
                v-model="editForm[field.key]"
                rows="3"
              />
              <select v-else-if="field.type === 'select'" v-model="editForm[field.key]">
                <option v-for="opt in field.options" :key="String(opt.value)" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <input v-else v-model="editForm[field.key]" :type="field.type || 'text'" />
            </label>
          </template>
        </template>
        <p v-if="saveError" class="manager-company-records-error">{{ saveError }}</p>
      </div>
      <div class="manager-company-records-modal-footer">
        <button type="button" class="manager-company-records-btn secondary" @click="onClose">Cancel</button>
        <button type="button" class="manager-company-records-btn primary" :disabled="saving" @click="$emit('save')">
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
      </div>
      <button
        type="button"
        class="manager-company-records-modal-resize"
        aria-label="Resize dialog"
        title="Drag to resize"
        @mousedown.prevent="onModalResizePointerDown"
        @touchstart.prevent="onModalResizePointerDown"
      />
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/DatePicker.vue';
import HRTimesheetAttachmentRow from '@/modules/consultant/components/HRTimesheetAttachmentRow.vue';
import {
  formatIsoToUs,
  parseUsToIso,
  partyNameOnly,
  needsStoredAttachmentActions
} from '../utils/managerTimesheetCardHelpers';
import '../styles/ManagerCompanyRecordsEditModal.css';

/** Same rule as CustomerCreateInvoice / CustomerAddNewInvoice: due = invoice date + payment term dueDays */
const INVOICE_DATE_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export default {
  name: 'ManagerCompanyRecordsEditModal',
  components: {
    DatePicker,
    HRTimesheetAttachmentRow
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    isTimesheetsView: {
      type: Boolean,
      default: false
    },
    isInvoicesView: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Edit'
    },
    editRow: {
      type: Object,
      default: null
    },
    editForm: {
      type: Object,
      required: true
    },
    recordEditFields: {
      type: Array,
      default: () => []
    },
    timesheetEditFields: {
      type: Array,
      default: () => []
    },
    canEditTimesheetPeriod: {
      type: Boolean,
      default: false
    },
    timesheetPeriodLockHint: {
      type: String,
      default: ''
    },
    editTimesheetListSource: {
      type: String,
      default: 'consultant'
    },
    timesheetAttachments: {
      type: Array,
      default: () => []
    },
    timesheetAttachmentsBusy: {
      type: Boolean,
      default: false
    },
    timesheetAttachmentError: {
      type: String,
      default: ''
    },
    timesheetAttachmentSuccess: {
      type: String,
      default: ''
    },
    saveError: {
      type: String,
      default: null
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'close',
    'save',
    'view-attachment',
    'download-attachment',
    'patch-edit-form',
    'add-timesheet-attachment',
    'delete-timesheet-attachment',
    'rename-timesheet-attachment'
  ],
  data() {
    return {
      modalResizeW: null,
      modalResizeH: null,
      modalResizeCleanupFn: null,
      invoiceDateDueSyncArmed: false,
      lastInvoiceDateForDueSync: '',
      tsAttRenameId: null,
      tsAttRenameValue: ''
    };
  },
  computed: {
    invoiceCustomerDisplay() {
      const c = this.editRow && this.editRow.customer;
      if (c && c.customerName) return c.customerName;
      return '—';
    },
    invoiceLineItems() {
      const items = this.editRow && this.editRow.items;
      return Array.isArray(items) ? items : [];
    },
    /** paymentTermId in DB links the row to payment_terms; API includes nested paymentTerm for display. */
    invoicePaymentTermDisplay() {
      const row = this.editRow;
      if (!row) return '—';
      const pt = row.paymentTerm;
      if (pt && typeof pt === 'object') {
        const name = pt.name != null && String(pt.name).trim() !== '' ? String(pt.name).trim() : '';
        const desc = pt.description != null && String(pt.description).trim() !== '' ? String(pt.description).trim() : '';
        if (name && desc) return `${name} (${desc})`;
        if (name) return name;
        if (desc) return desc;
      }
      return '—';
    },
    modalInlineStyle() {
      if (this.modalResizeW == null && this.modalResizeH == null) return {};
      const s = {};
      if (this.modalResizeW != null) s.width = `${this.modalResizeW}px`;
      if (this.modalResizeH != null) s.height = `${this.modalResizeH}px`;
      if (this.modalResizeH != null) s.maxHeight = 'none';
      return s;
    },
    editFormFromDateUI: {
      get() {
        return formatIsoToUs(this.editForm?.fromDate);
      },
      set(usDate) {
        this.$emit('patch-edit-form', { fromDate: parseUsToIso(usDate) });
      }
    },
    editFormToDateUI: {
      get() {
        return formatIsoToUs(this.editForm?.toDate);
      },
      set(usDate) {
        this.$emit('patch-edit-form', { toDate: parseUsToIso(usDate) });
      }
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.teardownModalResizeListeners();
        this.invoiceDateDueSyncArmed = false;
        this.lastInvoiceDateForDueSync = '';
        this.tsAttRenameId = null;
        this.tsAttRenameValue = '';
        return;
      }
      if (val && this.isInvoicesView) {
        this.invoiceDateDueSyncArmed = false;
        this.$nextTick(() => {
          this.lastInvoiceDateForDueSync = this.normalizeInvoiceDateIso(this.editForm?.date);
          this.invoiceDateDueSyncArmed = true;
        });
      }
    },
    'editForm.date'(newVal) {
      if (!this.show || !this.isInvoicesView || !this.invoiceDateDueSyncArmed) return;
      const iso = this.normalizeInvoiceDateIso(newVal);
      if (iso === this.lastInvoiceDateForDueSync) return;
      this.lastInvoiceDateForDueSync = iso;
      this.syncInvoiceDueDateFromPaymentTerm(iso);
    }
  },
  beforeUnmount() {
    this.teardownModalResizeListeners();
  },
  methods: {
    normalizeInvoiceDateIso(val) {
      if (val == null || val === '') return '';
      const s = String(val).trim();
      return s.length >= 10 ? s.slice(0, 10) : s;
    },
    /** Aligns with CustomerCreateInvoice.updateDueDate — uses nested paymentTerm from listInvoices. */
    syncInvoiceDueDateFromPaymentTerm(invoiceDateIso) {
      const term = this.editRow?.paymentTerm;
      if (!term || !invoiceDateIso || !INVOICE_DATE_ISO_RE.test(invoiceDateIso)) return;
      const days = Number(term.dueDays);
      if (!Number.isFinite(days) || days < 0) return;
      const d = new Date(`${invoiceDateIso}T12:00:00`);
      if (Number.isNaN(d.getTime())) return;
      d.setDate(d.getDate() + days);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      this.$emit('patch-edit-form', { dueDate: `${yyyy}-${mm}-${dd}` });
    },
    partyNameOnly,
    needsStoredAttachmentActions,
    getModalResizeClientPoint(e) {
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY, touch: true };
      }
      if (e.changedTouches && e.changedTouches.length) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY, touch: true };
      }
      return { x: e.clientX, y: e.clientY, touch: false };
    },
    onModalResizePointerDown(e) {
      const pt = this.getModalResizeClientPoint(e);
      if (!pt.touch && e.button !== 0) return;
      const el = this.$refs.modalRoot;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const startX = pt.x;
      const startY = pt.y;
      const startW = rect.width;
      const startH = rect.height;
      const minW = this.isTimesheetsView ? 360 : this.isInvoicesView ? 400 : 280;
      const minH = 200;
      const margin = 16;

      this.teardownModalResizeListeners();

      const move = (ev) => {
        if (ev.type === 'touchmove') ev.preventDefault();
        const p = this.getModalResizeClientPoint(ev);
        let w = startW + (p.x - startX);
        let h = startH + (p.y - startY);
        const maxW = window.innerWidth - margin;
        const maxH = window.innerHeight - margin;
        w = Math.min(maxW, Math.max(minW, w));
        h = Math.min(maxH, Math.max(minH, h));
        this.modalResizeW = Math.round(w);
        this.modalResizeH = Math.round(h);
      };

      const up = () => {
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', up);
        window.removeEventListener('touchcancel', up);
        this.modalResizeCleanupFn = null;
      };

      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      window.addEventListener('touchmove', move, { passive: false });
      window.addEventListener('touchend', up);
      window.addEventListener('touchcancel', up);
      this.modalResizeCleanupFn = up;
    },
    teardownModalResizeListeners() {
      if (this.modalResizeCleanupFn) {
        this.modalResizeCleanupFn();
      }
    },
    onClose() {
      this.tsAttRenameId = null;
      this.tsAttRenameValue = '';
      this.$emit('close');
    },
    pickTimesheetAttachmentFile() {
      this.$refs.tsAttachmentFileInput?.click();
    },
    onTsAttachmentFileChange(e) {
      const el = e.target;
      const file = el.files && el.files[0];
      el.value = '';
      if (!file) return;
      this.$emit('add-timesheet-attachment', { file });
    },
    startTimesheetAttachmentRename(att) {
      if (!att?.id) return;
      this.tsAttRenameId = att.id;
      this.tsAttRenameValue = att.fileName != null ? String(att.fileName) : '';
    },
    cancelTimesheetAttachmentRename() {
      this.tsAttRenameId = null;
      this.tsAttRenameValue = '';
    },
    submitTimesheetAttachmentRename(att) {
      const name = String(this.tsAttRenameValue || '').trim();
      if (!name || !att?.id) return;
      this.$emit('rename-timesheet-attachment', { attachmentId: att.id, fileName: name });
      this.tsAttRenameId = null;
      this.tsAttRenameValue = '';
    },
    formatTimesheetHoursReadonly(hours) {
      if (hours === '' || hours === null || hours === undefined) return '—';
      const n = Number(hours);
      return Number.isFinite(n) ? String(n) : '—';
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === '') return '—';
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2) : '—';
    },
    formatInvoiceQty(q) {
      if (q === null || q === undefined || q === '') return '—';
      const n = Number(q);
      return Number.isFinite(n) ? String(n) : '—';
    },
    isConsultantInvoiceLine(line) {
      return String(line?.itemType || '').toLowerCase() === 'consultant';
    },
    /** Consultant items bill by hours × hourly rate; quantity/unitPrice are often unused. */
    invoiceLineQtyOrHours(line) {
      if (this.isConsultantInvoiceLine(line)) {
        const h = line.hours;
        if (h !== null && h !== undefined && h !== '') {
          return this.formatInvoiceQty(h);
        }
      }
      return this.formatInvoiceQty(line.quantity);
    },
    invoiceLineUnitOrHourlyRate(line) {
      if (this.isConsultantInvoiceLine(line)) {
        const r = line.hourlyRate;
        if (r !== null && r !== undefined && r !== '') {
          return this.formatMoney(r);
        }
      }
      return this.formatMoney(line.unitPrice);
    },
    formatUsDate(value) {
      if (value === null || value === undefined || value === '') return '—';
      const s = String(value);
      const isoDay = s.length >= 10 ? s.slice(0, 10) : s;
      const d = value instanceof Date ? value : new Date(/^\d{4}-\d{2}-\d{2}$/.test(isoDay) ? `${isoDay}T12:00:00` : s);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    formatUsDateTime(value) {
      if (value === null || value === undefined || value === '') return '—';
      const d = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>
